import axios from 'axios';
import { Globals } from '@/modules/Globals';
import type { IStatsContext } from '@/interfaces/IStatsContext';
import { tzOffsetMs } from './gpthelp-tz';

/**
 * Business-day helpers for the GPTHelp host: timezone math (Intl-based,
 * DST-safe) plus the cached GET /stats/context fetch that feeds the context
 * `businessDay` node and the tools' local date defaults.
 */

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Instant at which `dateStr` (yyyy-MM-dd) reaches `hour`:00 local time in
 * `timeZone`. Two-pass offset resolution keeps DST transitions correct.
 */
export function zonedTimeToInstant(
  dateStr: string,
  timeZone: string,
  hour = 0
): Date {
  if (!DATE_ONLY_RE.test(dateStr)) {
    throw new Error(`Invalid date: "${dateStr}"`);
  }
  const utcGuess =
    new Date(`${dateStr}T00:00:00Z`).getTime() + hour * 3_600_000;
  const offset1 = tzOffsetMs(new Date(utcGuess), timeZone);
  const offset2 = tzOffsetMs(new Date(utcGuess - offset1), timeZone);
  return new Date(utcGuess - offset2);
}

/** Calendar date (yyyy-MM-dd) of `date` as seen in `timeZone`. */
export function dateStrInTimeZone(date: Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Adds `days` to a yyyy-MM-dd date label (timezone-less arithmetic). */
export function addDaysToDateStr(dateStr: string, days: number): string {
  if (!DATE_ONLY_RE.test(dateStr)) {
    throw new Error(`Invalid date: "${dateStr}"`);
  }
  const t = new Date(`${dateStr}T00:00:00Z`).getTime() + days * 86_400_000;
  return new Date(t).toISOString().slice(0, 10);
}

export interface IGpthelpBusinessDay {
  /** Current business date, yyyy-MM-dd in the branch timezone. */
  currentBusinessDate: string;
  /** Cutoff hour: sales before it belong to the previous business date. */
  startHour: number;
  /** Instant window of the current business day (ISO strings). */
  window: { from: string; to: string };
  source: 'api' | 'local';
}

/**
 * Local mirror of the server's business-date semantics: shift the instant
 * back by the cutoff hour, then take the calendar date in the branch TZ.
 */
export function computeLocalBusinessDay(params: {
  timeZone: string;
  startHour: number;
  now?: Date;
}): IGpthelpBusinessDay {
  const { timeZone } = params;
  const startHour = Number.isFinite(params.startHour)
    ? Math.max(0, Math.floor(params.startHour))
    : 0;
  const now = params.now ?? new Date();

  const currentBusinessDate = dateStrInTimeZone(
    new Date(now.getTime() - startHour * 3_600_000),
    timeZone
  );
  const from = zonedTimeToInstant(currentBusinessDate, timeZone, startHour);
  const to = zonedTimeToInstant(
    addDaysToDateStr(currentBusinessDate, 1),
    timeZone,
    startHour
  );

  return {
    currentBusinessDate,
    startHour,
    window: { from: from.toISOString(), to: to.toISOString() },
    source: 'local',
  };
}

interface IBusinessDayCacheEntry {
  value: IGpthelpBusinessDay;
  expiresAt: number;
}

const CACHE_TTL_MS = 5 * 60_000;
const businessDayCache = new Map<string, IBusinessDayCacheEntry>();
const businessDayInflight = new Map<string, Promise<IGpthelpBusinessDay>>();

/** Test hook: clears the module-level cache between specs. */
export function __resetGpthelpBusinessDayCache() {
  businessDayCache.clear();
  businessDayInflight.clear();
}

/**
 * Business-day node for the GPTHelp context: server-authoritative via
 * GET /stats/context (5-min cache, in-flight dedupe), local fallback when the
 * user lacks StatisticsView (the endpoint requires it) or the fetch fails.
 */
export async function getGpthelpBusinessDay(params: {
  restaurantId: number;
  branchId: number;
  timeZone: string;
  startHour: number;
  canViewStats: boolean;
}): Promise<IGpthelpBusinessDay> {
  const { restaurantId, branchId, timeZone, startHour, canViewStats } = params;

  if (!canViewStats) return computeLocalBusinessDay({ timeZone, startHour });

  const key = `${restaurantId}:${branchId}`;
  const cached = businessDayCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  let pending = businessDayInflight.get(key);
  if (!pending) {
    pending = (async (): Promise<IGpthelpBusinessDay> => {
      const res = await axios.get<IStatsContext>(
        `${Globals.apiPath}/stats/context?r=${restaurantId}&b=${branchId}`
      );
      const ctx = res.data;
      return {
        currentBusinessDate: ctx.currentBusinessDate,
        startHour: ctx.businessDayStartHour,
        window: { from: ctx.window.from, to: ctx.window.to },
        source: 'api',
      };
    })();
    businessDayInflight.set(key, pending);
    pending
      .catch(() => undefined)
      .finally(() => {
        if (businessDayInflight.get(key) === pending) {
          businessDayInflight.delete(key);
        }
      });
  }

  try {
    const value = await pending;
    businessDayCache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
    return value;
  } catch {
    return computeLocalBusinessDay({ timeZone, startHour });
  }
}
